import { Button, Stack, useTheme } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import dayjs from "dayjs";

import { VideoHeaderStyled, VideoShowStyled } from "./styled";

import { videoService } from "@/api/services/video";
import DashboardContent from "@/components/app/DashboardContent";
import DashboardHeader from "@/components/app/DashboardHeader";
import InitLoading from "@/components/app/Loading/InitLoading";
import ROUTE_CONSTANTS from "@/constants/route-constants";
import { IVideoShowResponse } from "@/api/types/video";
import { IParams } from "@/types/params";
import { getAxiosError } from "@/utils/get-axios-error";
import { useCheckPermissionAccess } from "@/hooks/useCheckPermissionAccess";
import { OperationAccess, PagesAccess } from "@/constants/enums/permissions";
import { durationFormatter } from "@/utils/duration-formatter";

const VideoShow = () => {
    const { id } = useParams<IParams>();
    const theme = useTheme();
    const videoRef = useRef<HTMLVideoElement>(null);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<IVideoShowResponse>();

    const listAccess = useCheckPermissionAccess(PagesAccess.VIDEO, OperationAccess.LIST);
    const editAccess = useCheckPermissionAccess(PagesAccess.VIDEO, OperationAccess.UPDATE);

    const getEditData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await videoService.show(Number(id));

            setData(response.data.data);
        } catch (err) {
            const error = getAxiosError(err);
            const message = error?.meta.message || "Server Error";

            enqueueSnackbar(message, { variant: "error" });
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (id) getEditData();

        return () => {
            navigator.mediaSession.metadata = new MediaMetadata({});
        };
    }, [id, getEditData]);

    useEffect(() => {
        if ("mediaSession" in navigator) {
            navigator.mediaSession.setActionHandler("play", () => {
                const video = videoRef.current;

                if (video) video.play();
            });

            navigator.mediaSession.setActionHandler("pause", () => {
                const video = videoRef.current;

                if (video) video.pause();
            });
        }
    }, []);

    if (loading) return <InitLoading />;

    if (!data) return <h5>Oops! Something Went Wrong!</h5>;

    return (
        <>
            <DashboardHeader
                title={`Show Video`}
                titleSuffix={
                    <Stack direction={"row"} spacing={2}>
                        {editAccess ? (
                            <Button
                                component={Link}
                                to={ROUTE_CONSTANTS.MEDIA_MANAGEMENT.VIDEOS.UPDATE.EDIT_BY_ID(data.id).ABSOLUTE}
                                variant="contained"
                            >
                                Edit Video
                            </Button>
                        ) : null}
                        {listAccess ? (
                            <Button component={Link} to={ROUTE_CONSTANTS.MEDIA_MANAGEMENT.VIDEOS.ROOT.ABSOLUTE}>
                                Back To Video List
                            </Button>
                        ) : null}
                    </Stack>
                }
            />
            <DashboardContent>
                <VideoHeaderStyled>
                    <video ref={videoRef} controls poster={data.thumbnail?.url} preload="true" src={data.playableUrl}>
                        <source src={data.playableUrl} type="video/mp4" />
                        <source src={data.assets.mp4} type="video/mp4" />
                        <source src={data.assets.hls} type="video/m3u8" />
                        <track default kind="captions" src="SUBTITLE_PATH" srcLang="en" />
                        Your browser does not support the video.
                    </video>
                    <h1>{data.title}</h1>
                    <p>{data.description}</p>
                </VideoHeaderStyled>
                <VideoShowStyled>
                    <div>
                        <span className="label">File Name</span>
                        <span>{data.title}</span>
                    </div>
                    {data.metadata?.duration !== undefined && (
                        <div>
                            <span className="label">Video Duration</span>
                            <span>{durationFormatter(data.metadata.duration)}</span>
                        </div>
                    )}
                    {data.metadata?.width && data.metadata?.height && (
                        <div>
                            <span className="label">Resolution</span>
                            <span>
                                {data.metadata.width}x{data.metadata.height} (
                                {data.metadata.height >= 2160
                                    ? "4K"
                                    : data.metadata.height >= 1440
                                        ? "1440p"
                                        : data.metadata.height >= 1080
                                            ? "1080p"
                                            : data.metadata.height >= 720
                                                ? "720p"
                                                : data.metadata.height >= 480
                                                    ? "480p"
                                                    : "SD"}
                                )
                            </span>
                        </div>
                    )}
                    {data.metadata?.videoCodec && (
                        <div>
                            <span className="label">Video Codec</span>
                            <span>{data.metadata.videoCodec.toUpperCase()}</span>
                        </div>
                    )}
                    {data.metadata?.framerate && (
                        <div>
                            <span className="label">Frame Rate</span>
                            <span>{data.metadata.framerate} fps</span>
                        </div>
                    )}
                    {data.metadata?.bitrate && (
                        <div>
                            <span className="label">Bitrate</span>
                            <span>{(data.metadata.bitrate / 1000).toFixed(2)} kbps</span>
                        </div>
                    )}
                    {data.metadata?.aspectRatio && (
                        <div>
                            <span className="label">Aspect Ratio</span>
                            <span>{data.metadata.aspectRatio}</span>
                        </div>
                    )}
                    <div>
                        <span className="label">Upload Date & Time</span>
                        <span>{dayjs(data.createdAt).format("YYYY-MM-DD HH:mm")}</span>
                    </div>
                    <div>
                        <span className="label">Published At</span>
                        <span>{dayjs(data.publishedAt).format("YYYY-MM-DD HH:mm")}</span>
                    </div>
                    <div>
                        <span className="label">Updated At</span>
                        <span>{dayjs(data.updatedAt).format("YYYY-MM-DD HH:mm")}</span>
                    </div>
                    {data.thumbnail && (
                        <div>
                            <span className="label">Thumbnail</span>
                            <span>
                                <img
                                    src={data.thumbnail.url}
                                    alt="Video thumbnail"
                                    style={{ maxWidth: "200px", maxHeight: "200px", borderRadius: "8px" }}
                                />
                            </span>
                        </div>
                    )}
                    {data.assignedTo &&
                        (() => {
                            const assignedToArray = Array.isArray(data.assignedTo) ? data.assignedTo : null;
                            const assignedToNumber = !Array.isArray(data.assignedTo) ? data.assignedTo : null;

                            return (
                                <div>
                                    <span className="label">Where Used (Content IDs)</span>
                                    <span>
                                        {assignedToArray ? (
                                            assignedToArray.length > 0 ? (
                                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                    {assignedToArray.map((contentId, index) => {
                                                        const isLast = index === assignedToArray.length - 1;
                                                        return (
                                                            <span key={index}>
                                                                <Link
                                                                    to={
                                                                        ROUTE_CONSTANTS.CONTENT_MANAGEMENT.CONTENTS.SHOW.SHOW_BY_ID(
                                                                            Number(contentId)
                                                                        ).ABSOLUTE
                                                                    }
                                                                    target="_blank"
                                                                    style={{
                                                                        color: theme.palette.primary.main,
                                                                        textDecoration: "underline",
                                                                    }}
                                                                >
                                                                    {contentId}
                                                                </Link>
                                                                {!isLast && ", "}
                                                            </span>
                                                        );
                                                    })}
                                                </Stack>
                                            ) : (
                                                "Not assigned"
                                            )
                                        ) : assignedToNumber ? (
                                            <Link
                                                to={
                                                    ROUTE_CONSTANTS.CONTENT_MANAGEMENT.CONTENTS.SHOW.SHOW_BY_ID(
                                                        Number(assignedToNumber)
                                                    ).ABSOLUTE
                                                }
                                                target="_blank"
                                                style={{ color: theme.palette.primary.main, textDecoration: "underline" }}
                                            >
                                                {assignedToNumber}
                                            </Link>
                                        ) : null}
                                    </span>
                                </div>
                            );
                        })()}
                </VideoShowStyled>
            </DashboardContent>
        </>
    );
};

export default VideoShow;
